// Image URL helpers for Supabase Storage
//
// Supabase supports on-the-fly image transformations via a separate render
// endpoint. The public object URL looks like:
//   https://{project}.supabase.co/storage/v1/object/public/photos/{path}
//
// The transform endpoint looks like:
//   https://{project}.supabase.co/storage/v1/render/image/public/photos/{path}?width=400...
//
// If the Image Transformation add-on is NOT enabled on the project, the
// render URL will return an error. Callers should use the <img onError>
// handler (see `thumbProps` below) to fall back to the raw URL so the grid
// keeps working either way.

export function getThumbnailUrl(rawUrl, options = {}) {
    if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
    const { width = 400, height = 400, resize = 'cover', quality = 75 } = options;

    // Only rewrite Supabase public object URLs; leave anything else alone.
    const marker = '/storage/v1/object/public/';
    if (!rawUrl.includes(marker)) return rawUrl;

    const transformed = rawUrl.replace(marker, '/storage/v1/render/image/public/');
    const separator = transformed.includes('?') ? '&' : '?';
    return `${transformed}${separator}width=${width}&height=${height}&resize=${resize}&quality=${quality}`;
}

// Convenience: returns the props needed for a thumbnail <img> tag, including
// the graceful fallback to the raw URL if the render endpoint fails (e.g.
// because Image Transformations isn't enabled on the project).
export function thumbProps(rawUrl, options = {}) {
    const thumb = getThumbnailUrl(rawUrl, options);
    return {
        src: thumb,
        onError: (e) => {
            // Fall back to the original full-res URL exactly once.
            if (e && e.currentTarget && e.currentTarget.src !== rawUrl) {
                e.currentTarget.src = rawUrl;
            }
        },
    };
}

// ---------------------------------------------------------------------------
// Upload helpers — used by every photo upload surface (walkthrough portal +
// office Client Intake + office Clients page). Originally lived inline in
// WalkthroughPortal.jsx; extracted here so all three surfaces share the same
// downscale + parallelism behavior. Single source of truth for "how a photo
// goes from File → Supabase Storage."
//
// Why these defaults: a 12MP iPhone photo at original resolution is 3-10 MB.
// At 2400px long edge JPEG q=0.85 it's ~400-700 KB while still showing
// enough detail for inventory / access / damage context. Concurrency=3 is
// the sweet spot for iOS Safari where each canvas decode is ~50 MB transient
// (3 × 50 = 150 MB peak, well under the ~256 MB tab budget).
// ---------------------------------------------------------------------------

export const DOWNSCALE_MAX_EDGE = 2400;        // px on the long edge
export const DOWNSCALE_QUALITY = 0.85;         // JPEG quality
export const DOWNSCALE_SKIP_BYTES = 800 * 1024; // pass small files through untouched
export const UPLOAD_CONCURRENCY = 3;           // simultaneous uploads in pool

// Convert HEIC/HEIF to JPEG via the heic2any library (browser-only —
// HEIC isn't decodable by <img> in non-Safari browsers, and even Safari
// needs the conversion to extract a Blob we can canvas-resize). Returns
// the original file unchanged for non-HEIC inputs OR on conversion
// failure (we'd rather upload the original than block the batch).
export async function convertHeicIfNeeded(file) {
    const isHeic = /\.(heic|heif)$/i.test(file.name) ||
                   file.type === 'image/heic' ||
                   file.type === 'image/heif';
    if (!isHeic) return file;

    try {
        const heic2any = (await import('heic2any')).default;
        const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: DOWNSCALE_QUALITY });
        return new File(
            [blob],
            file.name.replace(/\.(heic|heif)$/i, '.jpg'),
            { type: 'image/jpeg' },
        );
    } catch (e) {
        console.warn('[imageUtils] HEIC conversion failed, continuing with original:', e);
        return file;
    }
}

// Canvas-based resize. Bails fast on already-tiny files. On any error
// (decode failure, toBlob null, etc.) returns the original — the upload
// path is never blocked by a downscale problem.
export async function downscaleImage(file, opts = {}) {
    const maxEdge = opts.maxEdge || DOWNSCALE_MAX_EDGE;
    const quality = opts.quality || DOWNSCALE_QUALITY;
    const skipBytes = opts.skipBytes ?? DOWNSCALE_SKIP_BYTES;

    if (file.size <= skipBytes) return file;

    const objUrl = URL.createObjectURL(file);
    try {
        const img = await new Promise((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('image decode failed'));
            el.src = objUrl;
        });

        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const longEdge = Math.max(w, h);

        // If the source is already in budget AND already a JPEG, pass
        // through. PNG/WEBP/etc. still get re-encoded to JPEG so the
        // outbound payload normalizes.
        if (longEdge <= maxEdge && /^image\/jpe?g$/.test(file.type)) {
            return file;
        }

        const scale = Math.min(1, maxEdge / longEdge);
        const targetW = Math.max(1, Math.round(w * scale));
        const targetH = Math.max(1, Math.round(h * scale));

        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, targetW, targetH);

        const blob = await new Promise((resolve, reject) => {
            canvas.toBlob(
                b => b ? resolve(b) : reject(new Error('canvas toBlob failed')),
                'image/jpeg',
                quality,
            );
        });

        const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
        return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
    } catch (err) {
        console.warn('[imageUtils] downscale failed, uploading original:', err);
        return file;
    } finally {
        URL.revokeObjectURL(objUrl);
    }
}

// One-call convenience: HEIC → JPEG (if needed) → downscale. This is what
// every upload surface should run on each File before calling
// db.uploadPhoto, so the byte payload is bounded regardless of source.
export async function processImageForUpload(file, opts = {}) {
    const converted = await convertHeicIfNeeded(file);
    return downscaleImage(converted, opts);
}

// Run `worker(item)` over `items` with at most `concurrency` in flight at a
// time. Resolves once every item has been processed. Worker errors are the
// caller's responsibility — wrap the inside of `worker` in try/catch if you
// want per-item resilience (most callers do).
export async function uploadInPool(items, worker, concurrency = UPLOAD_CONCURRENCY) {
    // Workers also receive the item's ORIGINAL index (2026-09-11) so
    // uploads can stamp picker order — completion order is what made
    // galleries shuffle when three uploads ran at once.
    const queue = items.map((item, index) => ({ item, index }));
    const runners = [];
    const n = Math.min(concurrency, queue.length);
    for (let i = 0; i < n; i++) {
        runners.push((async () => {
            while (queue.length > 0) {
                const next = queue.shift();
                if (next === undefined) return;
                await worker(next.item, next.index);
            }
        })());
    }
    await Promise.all(runners);
}
